import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import { INTEGRATIONS_MAP } from "features/integrations/constants";
import type { OutreachSourceType } from "features/integrations/types";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useIntegrationMutations from "../datahooks/useIntegrationMutations";
import { IntegrationApiKeySchema } from "../schema";

interface EmailOutreachConnectProps {
  type: OutreachSourceType;
}

export default function EmailOutreachConnect({
  type,
}: EmailOutreachConnectProps) {
  const isDesktop = useTwBreakpoint("lg");
  const {
    connectEmailOutreachIntegration,
    isConnectingEmailOutreachIntegration,
  } = useIntegrationMutations();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: valibotResolver(IntegrationApiKeySchema),
    defaultValues: { api_key: "" },
  });

  function onSubmit({ api_key }: { api_key: string }) {
    connectEmailOutreachIntegration({ type, apiKey: api_key });
  }

  const { title } = INTEGRATIONS_MAP[type];

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component onAfterClose={reset}>
      <Trigger asChild>
        <Button variant="secondary-black" size="sm">
          Connect
        </Button>
      </Trigger>
      <Content>
        <Header title={<>Paste your {title} API key</>} />

        <Body>
          <FormField
            label="API key"
            htmlFor={`${type}-api-key`}
            hint="Keep your API key safe and do not share it with third parties"
            className="mb-8"
            error={errors.api_key?.message}
          >
            <Input
              id={`${type}-api-key`}
              placeholder="Enter your API key here"
              {...register("api_key")}
            />
          </FormField>
        </Body>

        <Footer className="justify-end">
          <Button
            onClick={handleSubmit(onSubmit)}
            isLoading={isConnectingEmailOutreachIntegration}
          >
            Save
          </Button>
        </Footer>
      </Content>
    </Component>
  );
}
